import { Component, EventEmitter, HostListener, Input, OnChanges, Output } from '@angular/core';

export interface VwDetailRow {
  label: string;
  value: string;
}

export interface VwDetailSection {
  title?: string;
  rows: VwDetailRow[];
}

/** Builds rows from [label, key] pairs, formatting empty values, booleans and ISO dates. */
export function vwDetailRows(data: any, fields: Array<[string, string]>): VwDetailRow[] {
  return fields.map(([label, key]) => ({ label, value: vwFormatDetail(data ? data[key] : null) }));
}

export function vwFormatDetail(value: any): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
    const d = new Date(value);
    if (!isNaN(d.getTime())) {
      return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
  }
  return String(value);
}

/** Sections for a visitor/guest record as returned by get_recall_guest_by_id. */
export function vwGuestDetailSections(g: any): VwDetailSection[] {
  if (!g) {
    return [];
  }
  const phone = g.contactNo ? ((g.dialingCode ? '+' + g.dialingCode + ' ' : '') + g.contactNo) : '';
  const sections: VwDetailSection[] = [
    {
      title: 'Visitor',
      rows: [
        { label: 'Name', value: vwFormatDetail(g.fullName) },
        { label: 'VIP', value: vwFormatDetail(g.isVip === true) },
        { label: 'Gender', value: vwFormatDetail(g.gender) },
        { label: 'Date of birth', value: vwFormatDetail(g.dob) },
        { label: 'Nationality', value: vwFormatDetail(g.nationality) },
        { label: 'Primary number', value: vwFormatDetail(phone) },
        { label: 'Email', value: vwFormatDetail(g.email) },
        { label: 'Address', value: vwFormatDetail(g.address) },
        { label: 'Document type', value: vwFormatDetail(g.documentType) },
        { label: 'Identity no', value: vwFormatDetail(g.documentId) },
        { label: 'Company', value: vwFormatDetail(g.companyName) },
      ]
    },
    {
      title: 'Visit',
      rows: [
        { label: 'Purpose of visit', value: vwFormatDetail(g.purposeOfVisit) },
        { label: 'Mode of entry', value: vwFormatDetail(g.mode) },
        { label: 'Booked date', value: vwFormatDetail(g.expectedDate) },
        { label: 'Status', value: vwFormatDetail(g.state) },
        { label: 'Host', value: vwFormatDetail(g.employeeName || g.residentName) },
        { label: 'Location', value: vwFormatDetail(g.flatNo) },
        { label: 'Booked vehicle no', value: vwFormatDetail(g.expectedVehicleNo) },
        { label: 'Vehicle model', value: vwFormatDetail(g.vehicleModel) },
        { label: 'QR validity (days)', value: vwFormatDetail(g.qrValidity) },
        { label: 'Checked in', value: vwFormatDetail(g.checkInTime) },
        { label: 'Checked out', value: vwFormatDetail(g.checkOutTime) },
        { label: 'Group', value: vwFormatDetail(g.groupName) },
      ]
    }
  ];
  (g.guestList || []).forEach((sg: any, i: number) => {
    sections.push({
      title: 'Secondary visitor ' + (i + 1),
      rows: vwDetailRows({
        ...sg,
        phone: sg && sg.contactNo ? ((sg.dialingCode ? '+' + sg.dialingCode + ' ' : '') + sg.contactNo) : ''
      }, [['Name', 'fullName'], ['Number', 'phone'], ['Document type', 'documentType'], ['Identity no', 'documentId'], ['Address', 'address'], ['Minor', 'minor']])
    });
  });
  (g.deviceList || []).forEach((d: any, i: number) => {
    sections.push({
      title: 'Gadget ' + (i + 1),
      rows: vwDetailRows(d, [['Name', 'deviceName'], ['Type', 'deviceType'], ['Serial no', 'serialNo'], ['Manufacturer', 'manufacturer'], ['Tag no', 'tagNo']])
    });
  });
  return sections;
}

function vwPhone(dialingCode: any, number: any): string {
  return number ? ((dialingCode ? '+' + String(dialingCode).replace('+', '') + ' ' : '') + number) : '';
}

function vwPerson(d: any): any {
  return {
    ...d,
    phone: vwPhone(d && d.dialingCode, d && d.contactNo),
    secPhone: vwPhone(d && d.secDialingCode, d && d.secContactNo),
    statusText: d && d.status === false ? 'Inactive' : (d && d.status === true ? 'Active' : '')
  };
}

/** Staff / employee-admin record (get_employee_by_id). */
export function vwEmployeeDetailSections(d: any): VwDetailSection[] {
  const p = vwPerson(d);
  return [
    { title: 'Personal', rows: vwDetailRows(p, [['Name', 'fullName'], ['Username', 'username'], ['Gender', 'gender'], ['Nationality', 'nationality'],
      ['Email', 'email'], ['Primary number', 'phone'], ['Secondary number', 'secPhone'], ['Country', 'country'], ['Address', 'address'],
      ['Document type', 'documentType'], ['Identity no', 'documentId']]) },
    { title: 'Work', rows: vwDetailRows(p, [['Employee ID', 'employeeId'], ['Profile', 'profile'], ['Employment', 'employment'],
      ['Shift time in', 'timeIn'], ['Shift time out', 'timeOut'], ['Working days', 'workingDays'], ['Host check-out', 'isHostCheckOut'], ['Status', 'statusText']]) }
  ];
}

/** Resident record (get_resident_by_id). */
export function vwResidentDetailSections(d: any): VwDetailSection[] {
  const p = vwPerson(d);
  return [
    { title: 'Personal', rows: vwDetailRows(p, [['Name', 'fullName'], ['Username', 'username'], ['Gender', 'gender'], ['Nationality', 'nationality'],
      ['Email', 'email'], ['Primary number', 'phone'], ['Secondary number', 'secPhone'], ['Country', 'country'], ['Address', 'address'],
      ['Document type', 'documentType'], ['Identity no', 'documentId'], ['Relationship', 'relationship'], ['Date of birth', 'dob']]) },
    { title: 'Account', rows: vwDetailRows(p, [['Type', 'type'], ['Status', 'statusText'], ['Notifications', 'notificationStatus'],
      ['Host check-out', 'isHostCheckOut'], ['Check-in approval', 'checkInStatus'], ['Staff notification', 'staffNotification']]) }
  ];
}

/** Domestic / office staff record (get_staff_by_id). */
export function vwStaffDetailSections(d: any): VwDetailSection[] {
  const p = vwPerson(d);
  return [
    { title: 'Personal', rows: vwDetailRows(p, [['Name', 'fullName'], ['Gender', 'gender'], ['Nationality', 'nationality'], ['Email', 'email'],
      ['Primary number', 'phone'], ['Country', 'country'], ['Address', 'address'], ['Document type', 'documentType'], ['Identity no', 'documentId']]) },
    { title: 'Work', rows: vwDetailRows(p, [['Employment', 'employment'], ['Company', 'companyName'], ['Company address', 'companyAddress'], ['Profile', 'profile'],
      ['Shift time in', 'timeIn'], ['Shift time out', 'timeOut'], ['Working days', 'workingDays'], ['Status', 'statusText']]) }
  ];
}

// Read-only "view details" popup, so a record can be inspected without opening
// its edit form. Presentational only: the caller loads the data and builds the sections.
@Component({
  selector: 'app-record-details-modal',
  templateUrl: 'record-details-modal.component.html',
  styleUrls: ['record-details-modal.component.scss']
})
export class RecordDetailsModalComponent implements OnChanges {
  @Input() open = false;
  @Input() title = 'Details';
  @Input() subtitle = '';
  @Input() loading = false;
  /** Optional picture (data URL) shown above the details. */
  @Input() imageSrc = '';
  @Input() sections: VwDetailSection[] = [];
  @Output() closed = new EventEmitter<void>();

  /** Only fields the server actually returned a value for; empty ones and empty sections are dropped. */
  visibleSections: VwDetailSection[] = [];

  ngOnChanges(): void {
    this.visibleSections = (this.sections || [])
      .map(section => ({ title: section.title, rows: section.rows.filter(row => row.value !== '—') }))
      .filter(section => section.rows.length > 0);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open) {
      this.closed.emit();
    }
  }
}
